import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DatePicker, Input, Loader, Message, toaster } from "rsuite";
import CloseIcon from "@rsuite/icons/Close";
import SaveIcon from "@rsuite/icons/Save";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import { PanelService } from "../../../network/panel/network";
import "./_caps.scss";

type CapType = "global" | "daily" | "monthly" | "custom";
type CapStatus = "active" | "complete";

interface Cap {
  _id: string;
  offerId?: number | null;
  amount: number;
  capType: CapType;
  status: CapStatus;
  datetimeStart?: string | null;
  datetimeEnd?: string | null;
  notifiedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

interface CapOffer {
  id: number;
  name: string;
}

interface CapFormState {
  offerId: string;
  amount: string;
  capType: CapType;
  datetimeStart: string;
  datetimeEnd: string;
}

interface OfferOption {
  value: number;
  name: string;
  searchLabel: string;
}

type SortColumn = "offerId" | "amount" | "capType" | "status" | "datetimeStart" | "datetimeEnd";
type SortDirection = "asc" | "desc";

const CAP_TYPE_LABELS: Record<CapType, string> = {
  global: "Глобальна",
  daily: "Денна",
  monthly: "Місячна",
  custom: "Власний період",
};

const CAP_STATUS_LABELS: Record<CapStatus, string> = {
  active: "Активна",
  complete: "Завершена",
};

const DEFAULT_FORM: CapFormState = {
  offerId: "",
  amount: "",
  capType: "global",
  datetimeStart: "",
  datetimeEnd: "",
};

function getOfferDisplayLabel(offerId: number, offerName?: string) {
  return `#${offerId} ${offerName || "Офер не знайдено"}`;
}

function formatDateTime(value?: string | null) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleString("uk-UA", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getSortValue(cap: Cap, sortColumn: SortColumn) {
  if (sortColumn === "offerId") return cap.offerId ?? -1;
  if (sortColumn === "amount") return cap.amount;
  if (sortColumn === "capType") return cap.capType;
  if (sortColumn === "status") return cap.status;
  if (sortColumn === "datetimeStart") return cap.datetimeStart || "";
  return cap.datetimeEnd || "";
}

export default function Caps() {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const offerComboboxRef = useRef<HTMLDivElement | null>(null);

  const [caps, setCaps] = useState<Cap[]>([]);
  const [offers, setOffers] = useState<CapOffer[]>([]);
  const [loading, setLoading] = useState(true);
  const [offersLoading, setOffersLoading] = useState(true);
  const [offersLoaded, setOffersLoaded] = useState(false);
  const [search, setSearch] = useState("");
  const [sortColumn, setSortColumn] = useState<SortColumn>("offerId");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingCap, setEditingCap] = useState<Cap | null>(null);
  const [form, setForm] = useState<CapFormState>(DEFAULT_FORM);
  const [offerQuery, setOfferQuery] = useState("");
  const [offerMenuOpen, setOfferMenuOpen] = useState(false);

  const offerNameById = useMemo(
    () => new Map(offers.map((offer) => [offer.id, offer.name])),
    [offers]
  );

  const offerOptions = useMemo<OfferOption[]>(() => {
    const options = offers.map((offer) => ({
      value: offer.id,
      name: offer.name,
      searchLabel: `${offer.id} ${offer.name}`.toLowerCase(),
    }));

    const selectedId = Number(form.offerId);
    if (
      form.offerId.trim() &&
      Number.isInteger(selectedId) &&
      selectedId > 0 &&
      !options.some((option) => option.value === selectedId)
    ) {
      options.unshift({
        value: selectedId,
        name: "Офер не знайдено в Keitaro",
        searchLabel: String(selectedId),
      });
    }

    return options;
  }, [offers, form.offerId]);

  const selectedOfferId = form.offerId.trim() ? Number(form.offerId) : null;
  const selectedOffer = selectedOfferId ? offerOptions.find((option) => option.value === selectedOfferId) : null;
  const filteredOfferOptions = useMemo(() => {
    const normalizedQuery = offerQuery.trim().toLowerCase();
    const sortedOffers = [...offerOptions].sort((left, right) => left.value - right.value);

    if (!normalizedQuery) return sortedOffers;
    if (selectedOffer && normalizedQuery === getOfferDisplayLabel(selectedOffer.value, selectedOffer.name).toLowerCase()) {
      return sortedOffers;
    }

    return sortedOffers.filter((option) => option.searchLabel.includes(normalizedQuery));
  }, [offerOptions, offerQuery, selectedOffer]);

  const loadCapsPage = async () => {
    setLoading(true);
    setOffersLoading(true);

    try {
      const [capsData, offersData] = await Promise.all([PanelService.getCaps(), PanelService.getCapsOffers()]);
      setCaps(Array.isArray(capsData) ? capsData : []);
      setOffers(Array.isArray(offersData) ? offersData : []);
      setOffersLoaded(true);
    } catch (_) {
      setOffersLoaded(false);
      toaster.push(<Message type="error">Не вдалося завантажити капи або офери</Message>, {
        duration: 3500,
      });
    } finally {
      setLoading(false);
      setOffersLoading(false);
    }
  };

  useEffect(() => {
    void loadCapsPage();
  }, []);

  useEffect(() => {
    if (!modalOpen) return;

    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (saving) return;
        setModalOpen(false);
        setOfferMenuOpen(false);
      }
    };

    const handlePointerDown = (event: MouseEvent) => {
      if (!offerComboboxRef.current?.contains(event.target as Node)) {
        setOfferMenuOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscKey);
    document.addEventListener("mousedown", handlePointerDown);

    return () => {
      document.removeEventListener("keydown", handleEscKey);
      document.removeEventListener("mousedown", handlePointerDown);
    };
  }, [modalOpen, saving]);

  const filteredCaps = useMemo(() => {
    const query = search.trim().toLowerCase();
    const nextCaps = !query
      ? caps
      : caps.filter((cap) => {
          const offerName = cap.offerId ? offerNameById.get(cap.offerId) || "" : "";

          return [
            String(cap.offerId ?? ""),
            offerName,
            String(cap.amount),
            CAP_TYPE_LABELS[cap.capType],
            CAP_STATUS_LABELS[cap.status],
          ].some((value) => value.toLowerCase().includes(query));
        });

    return [...nextCaps].sort((left, right) => {
      const a = getSortValue(left, sortColumn);
      const b = getSortValue(right, sortColumn);

      if (a < b) return sortDirection === "asc" ? -1 : 1;
      if (a > b) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [caps, search, sortColumn, sortDirection, offerNameById]);

  const setFormField = (field: keyof CapFormState, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const openCreateModal = () => {
    if (offersLoading || !offersLoaded) {
      toaster.push(<Message type="warning">Офери ще завантажуються. Спробуйте за мить.</Message>, {
        duration: 2500,
      });
      return;
    }

    setEditingCap(null);
    setForm(DEFAULT_FORM);
    setOfferQuery("");
    setOfferMenuOpen(false);
    setModalOpen(true);
  };

  const openEditModal = (cap: Cap) => {
    if (offersLoading || !offersLoaded) {
      toaster.push(<Message type="warning">Офери ще завантажуються. Спробуйте за мить.</Message>, {
        duration: 2500,
      });
      return;
    }

    setEditingCap(cap);
    const offerLabel = cap.offerId ? getOfferDisplayLabel(cap.offerId, offerNameById.get(cap.offerId)) : "";
    setForm({
      offerId: cap.offerId ? String(cap.offerId) : "",
      amount: String(cap.amount ?? ""),
      capType: cap.capType,
      datetimeStart: cap.datetimeStart || "",
      datetimeEnd: cap.datetimeEnd || "",
    });
    setOfferQuery(offerLabel);
    setOfferMenuOpen(false);
    setModalOpen(true);
  };

  const closeModal = useCallback(() => {
    if (saving) return;
    setModalOpen(false);
    setOfferMenuOpen(false);
  }, [saving]);

  const handleOfferQueryChange = (value: string) => {
    setOfferQuery(value);
    setFormField("offerId", "");
    setOfferMenuOpen(true);
  };

  const handleSelectOffer = (offer: OfferOption) => {
    setFormField("offerId", String(offer.value));
    setOfferQuery(getOfferDisplayLabel(offer.value, offer.name));
    setOfferMenuOpen(false);
  };

  const serializeForm = () => {
    const offerId = form.offerId.trim();
    const amount = Number(form.amount);

    if (!offerId) {
      throw new Error("Потрібно вибрати офер");
    }

    if (!Number.isInteger(Number(offerId)) || Number(offerId) < 1) {
      throw new Error("Потрібно вибрати коректний офер");
    }

    if (!Number.isFinite(amount) || amount < 1) {
      throw new Error("Ліміт має бути додатним числом");
    }

    if (form.capType === "custom") {
      if (!form.datetimeStart || !form.datetimeEnd) {
        throw new Error("Для власного періоду потрібно вказати дату початку і завершення");
      }

      if (new Date(form.datetimeStart) > new Date(form.datetimeEnd)) {
        throw new Error("Дата початку має бути раніше дати завершення");
      }
    }

    return {
      offerId: Number(offerId),
      amount,
      capType: form.capType,
      datetimeStart: form.capType === "custom" ? new Date(form.datetimeStart).toISOString() : null,
      datetimeEnd: form.capType === "custom" ? new Date(form.datetimeEnd).toISOString() : null,
    };
  };

  const handleSaveCap = async () => {
    setSaving(true);

    try {
      const payload = serializeForm();
      const savedCap = editingCap
        ? await PanelService.updateCap(editingCap._id, payload)
        : await PanelService.createCap(payload);

      setCaps((prev) => {
        if (!editingCap) {
          return [savedCap, ...prev];
        }

        return prev.map((cap) => (cap._id === editingCap._id ? savedCap : cap));
      });

      toaster.push(
        <Message type="success">{editingCap ? "Капу оновлено" : "Капу додано"}</Message>,
        { duration: 2200 }
      );
      setModalOpen(false);
      setEditingCap(null);
      setForm(DEFAULT_FORM);
    } catch (error: any) {
      toaster.push(
        <Message type="error">
          {error?.response?.data?.error || error?.message || "Не вдалося зберегти капу"}
        </Message>,
        { duration: 3500 }
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCap = async () => {
    if (!editingCap) return;
    if (!window.confirm("Видалити цю капу?")) return;

    setSaving(true);
    try {
      await PanelService.deleteCap(editingCap._id);
      setCaps((prev) => prev.filter((cap) => cap._id !== editingCap._id));
      toaster.push(<Message type="success">Капу видалено</Message>, { duration: 2200 });
      setModalOpen(false);
      setEditingCap(null);
      setForm(DEFAULT_FORM);
    } catch (error: any) {
      toaster.push(
        <Message type="error">{error?.response?.data?.error || "Не вдалося видалити капу"}</Message>,
        { duration: 3500 }
      );
    } finally {
      setSaving(false);
    }
  };

  const toggleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }

    setSortColumn(column);
    setSortDirection("asc");
  };

  const renderSortLabel = (label: string, column: SortColumn) => {
    if (sortColumn !== column) {
      return (
        <>
          {label} <span className="caps-sort-icon">↕</span>
        </>
      );
    }

    return (
      <>
        {label} <span className="caps-sort-icon active">{sortDirection === "asc" ? "↑" : "↓"}</span>
      </>
    );
  };

  const getPickerContainer = () => dialogRef.current || document.body;
  return (
    <div className="caps-page">
      <div className="caps-header">
        <div className="caps-header-content">
          <div className="lists-search">
            <input
              type="text"
              placeholder="Пошук по ID, назві, типу або статусу"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="lists-search-input"
            />
            <FontAwesomeIcon icon={faSearch} className="lists-search-icon" />
          </div>

          <div className="caps-actions">
            <button
              className="lists-tab create-cap-btn"
              onClick={openCreateModal}
              disabled={loading || offersLoading || !offersLoaded}
            >
              {offersLoading ? "Завантаження оферів..." : "Додати капу"}
            </button>
          </div>
        </div>
      </div>

      <div className="caps-content">
        {loading ? (
          <div className="caps-state">
            <Loader size="md" content="Завантаження капів та оферів" vertical />
          </div>
        ) : (
          <div className="caps-table-wrap">
            <table className="caps-table">
              <thead>
                <tr>
                  <th onClick={() => toggleSort("offerId")}>{renderSortLabel("Офер", "offerId")}</th>
                  <th onClick={() => toggleSort("amount")}>{renderSortLabel("Ліміт", "amount")}</th>
                  <th onClick={() => toggleSort("capType")}>{renderSortLabel("Тип", "capType")}</th>
                  <th onClick={() => toggleSort("status")}>{renderSortLabel("Статус", "status")}</th>
                  <th onClick={() => toggleSort("datetimeStart")}>{renderSortLabel("Початок", "datetimeStart")}</th>
                  <th onClick={() => toggleSort("datetimeEnd")}>{renderSortLabel("Завершення", "datetimeEnd")}</th>
                </tr>
              </thead>
              <tbody>
                {filteredCaps.length === 0 ? (
                  <tr>
                    <td className="caps-empty" colSpan={6}>
                      Капи не знайдено
                    </td>
                  </tr>
                ) : (
                  filteredCaps.map((cap) => {
                    const offerName = cap.offerId
                      ? offerNameById.get(cap.offerId) || "Офер не знайдено"
                      : "Офер не знайдено";

                    return (
                      <tr
                        key={cap._id}
                        className="caps-row"
                        onClick={() => openEditModal(cap)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            openEditModal(cap);
                          }
                        }}
                        tabIndex={0}
                      >
                        <td>
                          <div className="caps-offer-cell">
                            <span className="caps-offer-id">{cap.offerId ? `#${cap.offerId}` : "—"}</span>
                            <span className="caps-offer-name">{offerName}</span>
                          </div>
                        </td>
                        <td>{cap.amount}</td>
                        <td>
                          <span className={`caps-badge caps-badge-type caps-badge-${cap.capType}`}>
                            {CAP_TYPE_LABELS[cap.capType]}
                          </span>
                        </td>
                        <td>
                          <span className={`caps-badge caps-badge-status caps-badge-${cap.status}`}>
                            {CAP_STATUS_LABELS[cap.status]}
                          </span>
                        </td>
                        <td>{formatDateTime(cap.datetimeStart)}</td>
                        <td>{formatDateTime(cap.datetimeEnd)}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modalOpen && (
        <div className="caps-dialog-backdrop" onClick={(event) => event.target === event.currentTarget && closeModal()}>
          <div className="caps-dialog-shell" ref={dialogRef} onClick={(event) => event.stopPropagation()}>
            <div className="caps-dialog-header">
              <div className="caps-dialog-title-wrap">
                <h2 className="caps-dialog-title">{editingCap ? "Редагувати капу" : "Додати капу"}</h2>
              </div>

              <div className="caps-dialog-actions">
                <button className="lists-tab caps-save-btn" onClick={handleSaveCap} disabled={saving || offersLoading}>
                  <SaveIcon />
                  {saving ? "Збереження..." : "Зберегти"}
                </button>
                <button className="lists-tab caps-close-btn" onClick={closeModal} disabled={saving}>
                  <CloseIcon />
                  Закрити
                </button>
              </div>
            </div>

            <div className="caps-dialog-content">
              <div className="caps-dialog-panel">
                <div className="caps-dialog-panel-title">Основні параметри</div>

                <div className="caps-modal-grid">
                  <label className="caps-field caps-field-full">
                    <span className="caps-field-label">Офер</span>
                    <div className="caps-offer-combobox" ref={offerComboboxRef}>
                      <div className={`caps-offer-combobox-input ${offerMenuOpen ? "is-open" : ""}`}>
                        <input
                          type="text"
                          value={offerQuery}
                          onChange={(event) => handleOfferQueryChange(event.target.value)}
                          onFocus={() => setOfferMenuOpen(true)}
                          className="caps-offer-search-input"
                          placeholder={offersLoading ? "Завантаження оферів..." : "Почніть вводити ID або назву офера"}
                          disabled={offersLoading || !offersLoaded}
                        />
                        <button
                          type="button"
                          className="caps-offer-combobox-caret"
                          onClick={() => setOfferMenuOpen((prev) => !prev)}
                          disabled={offersLoading || !offersLoaded}
                          aria-label="Відкрити список оферів"
                        >
                          ▾
                        </button>
                      </div>

                      {offerMenuOpen && (
                        <div className="caps-offer-combobox-menu">
                          {filteredOfferOptions.length === 0 ? (
                            <div className="caps-offer-combobox-empty">Нічого не знайдено</div>
                          ) : (
                            filteredOfferOptions.map((option) => (
                              <button
                                key={option.value}
                                type="button"
                                className={`caps-offer-combobox-option ${
                                  selectedOffer?.value === option.value ? "is-selected" : ""
                                }`}
                                onClick={() => handleSelectOffer(option)}
                              >
                                <span className="caps-offer-option-id">#{option.value}</span>
                                <span className="caps-offer-option-name">{option.name}</span>
                              </button>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  </label>

                  <label className="caps-field">
                    <span className="caps-field-label">Ліміт</span>
                    <Input
                      type="number"
                      min={1}
                      value={form.amount}
                      onChange={(value) => setFormField("amount", value)}
                      className="caps-modal-input"
                      placeholder="Наприклад, 50"
                    />
                  </label>

                  <label className="caps-field">
                    <span className="caps-field-label">Тип капи</span>
                    <div className="caps-native-select-wrap">
                      <select
                        className="caps-native-select"
                        value={form.capType}
                        onChange={(event) => setFormField("capType", event.target.value)}
                      >
                        {Object.entries(CAP_TYPE_LABELS).map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </select>
                      <span className="caps-native-select-caret">▾</span>
                    </div>
                  </label>
                </div>
              </div>

              {form.capType === "custom" && (
                <div className="caps-dialog-panel">
                  <div className="caps-dialog-panel-title">Розклад</div>

                  <div className="caps-modal-grid two-col">
                    <label className="caps-field">
                      <span className="caps-field-label">Дата початку</span>
                      <DatePicker
                        oneTap
                        format="dd.MM.yyyy HH:mm"
                        value={form.datetimeStart ? new Date(form.datetimeStart) : null}
                        onChange={(value) => setFormField("datetimeStart", value ? value.toISOString() : "")}
                        block
                        editable={false}
                        placeholder="Оберіть дату і час"
                        menuClassName="caps-picker-menu"
                        container={getPickerContainer}
                      />
                    </label>

                    <label className="caps-field">
                      <span className="caps-field-label">Дата завершення</span>
                      <DatePicker
                        oneTap
                        format="dd.MM.yyyy HH:mm"
                        value={form.datetimeEnd ? new Date(form.datetimeEnd) : null}
                        onChange={(value) => setFormField("datetimeEnd", value ? value.toISOString() : "")}
                        block
                        editable={false}
                        placeholder="Оберіть дату і час"
                        menuClassName="caps-picker-menu"
                        container={getPickerContainer}
                      />
                    </label>
                  </div>
                </div>
              )}
            </div>

            {editingCap && (
              <div className="caps-dialog-footer">
                <button className="lists-tab caps-delete-btn" onClick={handleDeleteCap} disabled={saving}>
                  <FontAwesomeIcon icon={faTrashAlt} />
                  Видалити
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
